import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';
import moment from 'moment';

import * as graphql from 'generated/graphql';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import PlusIcon from 'packages/kakapo-ui-kit/assets/icons/PlusIcon';
import HashLight from 'packages/kakapo-ui-kit/components/common/HashLight';
import Clip from 'packages/kakapo-ui-kit/components/common/Clip';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import TrashIcon from 'packages/kakapo-ui-kit/assets/icons/TrashIcon';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

import EvaluationNoteModal from './EvaluationNoteModal';
import EvaluationNoteComposerModal from './EvaluationNoteComposerModal';
import useDetectPrint from 'conversations/evaluation-submission/hooks/useDetectPrint';

const Container = styled.div`
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	background: white;
	border-radius: 4px;
`;

const ArchiveNoteButton = styled.button`
	border: none;
	outline: none;
	background-color: transparent;
`;

const Note = styled.div<{ first: boolean }>`
	padding: 10px;
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};

	${(props) => props.first && `border-top: none;`}
`;

const Name = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
	font-weight: bold;
`;

const Timestamp = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
`;

const Text = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
`;

const AddNote = styled.div`
	padding: 5px 10px;
	cursor: pointer;
	color: ${(props) => props.theme.colors.slateBlue};
	line-height: normal;
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};

	svg * {
		fill: ${(props) => props.theme.colors.slateBlue};
	}

	&:hover {
		background: ${(props) => props.theme.colors.lightBlue};

		&:active {
			background: black;
			color: white;

			svg * {
				fill: white;
			}
		}
	}

	&:first-child {
		border-top: none;
	}
`;

function ArchiveButton({
	commentID,
	hover,
	onArchiveNote,
}: {
	commentID: string;
	hover: boolean;
	onArchiveNote: () => void;
}) {
	const theme = useTheme();
	const { t } = useTranslation();
	const [isRemoving, setIsRemoving] = useState<boolean>(false);

	const [archive] = graphql.useArchiveCommentMutation();

	if (!hover && !isRemoving) {
		return null;
	}

	return (
		<ArchiveNoteButton
			type="button"
			onClick={async () => {
				if (isRemoving) {
					return;
				}

				// eslint-disable-next-line no-alert
				const confirmed = window.confirm(t('comments.item.remove.buttonTitle'));

				if (!confirmed) {
					return;
				}

				setIsRemoving(true);

				await archive({
					variables: {
						input: {
							id: commentID,
						},
					},
					onCompleted: () => {
						onArchiveNote();
					},
				});
			}}
		>
			<Flex flexShrink={0}>
				{isRemoving ? (
					<Spinner isLoading size="16px" verticalMargin={0} />
				) : (
					<TrashIcon color={theme.colors.slateBlue} />
				)}
			</Flex>
		</ArchiveNoteButton>
	);
}

function EvaluationNote(props: {
	comment: graphql.CommentFragment;
	first: boolean;
	onArchiveNote: () => void;
}) {
	const [modal, setModal] = useState(false);
	const [hover, setHover] = useState(false);

	const { isWorkspaceSuperuser } = useViewerRoles();
	const authenticatedUser = useAuthenticatedUser();

	const canRemove = isWorkspaceSuperuser || props.comment.user.id === authenticatedUser.id;

	return (
		<>
			<HashLight name={`comment-${props.comment.id}`}>
				<Note
					first={props.first}
					onMouseEnter={() => setHover(true)}
					onMouseLeave={() => setHover(false)}
				>
					<Flex mb={3}>
						<Box flexGrow={1} flexShrink={1}>
							<Name>
								{props.comment.user.firstName} {props.comment.user.lastName}
							</Name>
						</Box>
						<Box flexGrow={0} flexShrink={0}>
							<Flex>
								{canRemove && (
									<ArchiveButton
										commentID={props.comment.id}
										onArchiveNote={props.onArchiveNote}
										hover={hover}
									/>
								)}
								<Box ml={2}>
									<Link to={`#comment-${props.comment.id}`}>
										<Timestamp>{moment(props.comment.createdAt).format('DD MMM')}</Timestamp>
									</Link>
								</Box>
							</Flex>
						</Box>
					</Flex>
					<Clip onClick={() => setModal(true)}>
						<Text>
							<QuillText quill={props.comment.content} />
						</Text>
					</Clip>
				</Note>
			</HashLight>
			{modal && <EvaluationNoteModal comment={props.comment} onClose={() => setModal(false)} />}
		</>
	);
}

export default function EvaluationNotes(props: {
	submission: graphql.EvaluationSubmissionFragment;
	itemID?: string;
	onNotesChange: () => void;
}) {
	const { t } = useTranslation();
	const isPrinting = useDetectPrint();

	const [modal, setModal] = useState(false);

	const comments = props.submission.comments
		.filter(
			(comment) =>
				new Map(comment.metadata?.map((metadata) => [metadata.key, metadata.value])).get(
					'formItemID',
				) === props.itemID,
		)
		.filter((comment) => !comment.isArchived);

	if (isPrinting || (!props.submission.viewer.viewerCanUpdate && comments.length === 0)) {
		return null;
	}

	return (
		<Container>
			{[...comments].reverse().map((comment, index) => (
				<EvaluationNote
					key={comment.id}
					comment={comment}
					first={index === 0}
					onArchiveNote={props.onNotesChange}
				/>
			))}
			{!isPrinting && props.submission.viewer.viewerCanUpdate && props.itemID && (
				<AddNote onClick={() => setModal(true)}>
					<Flex alignItems="center">
						<Box flexGrow={0} flexShrink={0} mr={1}>
							<PlusIcon style={{ display: 'block' }} />
						</Box>
						<Box flexGrow={0} flexShrink={0}>
							{t('evaluation.submission.notes.addButton.title')}
						</Box>
					</Flex>
				</AddNote>
			)}
			{modal && (
				<EvaluationNoteComposerModal
					submissionID={props.submission.id}
					itemID={props.itemID}
					onDismiss={() => setModal(false)}
					onNotesChange={() => {
						props.onNotesChange();
						setModal(false);
					}}
				/>
			)}
		</Container>
	);
}
